import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Milk, Moon, TrendingUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format, subDays, differenceInMinutes } from "date-fns";

interface FeedingSleepTrackerProps {
  babyId: string;
}

export function FeedingSleepTracker({ babyId }: FeedingSleepTrackerProps) {
  const [feedingType, setFeedingType] = useState("breast_both");
  const [duration, setDuration] = useState("");
  const [amount, setAmount] = useState("");
  const [sleepStart, setSleepStart] = useState("");
  const [sleepEnd, setSleepEnd] = useState("");
  const [sleepQuality, setSleepQuality] = useState("peaceful");
  const [feedingData, setFeedingData] = useState<any[]>([]);
  const [sleepData, setSleepData] = useState<any[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, [babyId]);

  const loadData = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const sevenDaysAgo = subDays(new Date(), 7).toISOString();

    const { data: feeding } = await supabase
      .from("baby_feeding")
      .select("*")
      .eq("user_id", user.id)
      .eq("baby_id", babyId)
      .gte("feeding_time", sevenDaysAgo)
      .order("feeding_time", { ascending: false });

    const { data: sleep } = await supabase
      .from("baby_sleep")
      .select("*")
      .eq("user_id", user.id)
      .eq("baby_id", babyId)
      .gte("sleep_start", sevenDaysAgo)
      .order("sleep_start", { ascending: false });

    if (feeding) setFeedingData(feeding);
    if (sleep) setSleepData(sleep);
  };

  const handleLogFeeding = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from("baby_feeding")
      .insert({
        user_id: user.id,
        baby_id: babyId,
        feeding_time: new Date().toISOString(),
        feeding_type: feedingType,
        duration_minutes: duration ? parseInt(duration) : null,
        amount_ml: amount ? parseInt(amount) : null,
      });

    if (error) {
      toast({ title: "Error", description: "Failed to log feeding", variant: "destructive" });
      return;
    }

    toast({ title: "Feeding logged!", description: "Feeding has been recorded" });
    setDuration("");
    setAmount("");
    loadData();
  };

  const handleLogSleep = async () => {
    if (!sleepStart || !sleepEnd) {
      toast({ title: "Missing information", description: "Please enter start and end times", variant: "destructive" });
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from("baby_sleep")
      .insert({
        user_id: user.id,
        baby_id: babyId,
        sleep_start: sleepStart,
        sleep_end: sleepEnd,
        sleep_quality: sleepQuality,
      });

    if (error) {
      toast({ title: "Error", description: "Failed to log sleep", variant: "destructive" });
      return;
    }

    toast({ title: "Sleep logged!", description: "Sleep session has been recorded" });
    setSleepStart("");
    setSleepEnd("");
    loadData();
  };

  const calculateAverageFeedings = () => {
    if (feedingData.length === 0) return 0;
    const dailyCounts: { [key: string]: number } = {};
    feedingData.forEach((feed) => {
      const day = format(new Date(feed.feeding_time), "yyyy-MM-dd");
      dailyCounts[day] = (dailyCounts[day] || 0) + 1;
    });
    const days = Object.keys(dailyCounts).length;
    return days > 0 ? (feedingData.length / days).toFixed(1) : 0;
  };

  const calculateAverageSleep = () => {
    if (sleepData.length === 0) return 0;
    const totalMinutes = sleepData.reduce((acc, sleep) => {
      if (sleep.sleep_end) {
        return acc + differenceInMinutes(new Date(sleep.sleep_end), new Date(sleep.sleep_start));
      }
      return acc;
    }, 0);
    return (totalMinutes / 60).toFixed(1);
  };

  return (
    <div className="space-y-6">
      <Card className="p-4 bg-secondary/30">
        <h3 className="font-semibold mb-2 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          Understanding Patterns
        </h3>
        <p className="text-sm text-muted-foreground">
          Tracking feeding and sleep helps you identify patterns, predict needs, and understand your baby's rhythms. 
          This data reveals what works best for your baby, helps establish routines, and can be valuable information 
          for healthcare providers. You'll see trends emerge that help you make informed decisions about care.
        </p>
      </Card>

      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Milk className="h-5 w-5 text-primary" />
            <h3 className="font-semibold">Average Feedings/Day</h3>
          </div>
          <p className="text-3xl font-bold">{calculateAverageFeedings()}</p>
          <p className="text-sm text-muted-foreground">Last 7 days</p>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Moon className="h-5 w-5 text-primary" />
            <h3 className="font-semibold">Average Sleep/Day</h3>
          </div>
          <p className="text-3xl font-bold">{calculateAverageSleep()}h</p>
          <p className="text-sm text-muted-foreground">Last 7 days</p>
        </Card>
      </div>

      <Tabs defaultValue="feeding">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="feeding">Feeding</TabsTrigger>
          <TabsTrigger value="sleep">Sleep</TabsTrigger>
        </TabsList>

        <TabsContent value="feeding" className="space-y-4">
          <Card className="p-4">
            <h3 className="font-semibold mb-4">Log Feeding</h3>
            <div className="space-y-4">
              <div>
                <Label>Type</Label>
                <Select value={feedingType} onValueChange={setFeedingType}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="breast_left">Breast (Left)</SelectItem>
                    <SelectItem value="breast_right">Breast (Right)</SelectItem>
                    <SelectItem value="breast_both">Breast (Both)</SelectItem>
                    <SelectItem value="bottle">Bottle</SelectItem>
                    <SelectItem value="solid">Solid Food</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Duration (minutes)</Label>
                <Input
                  type="number"
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  placeholder="Optional"
                />
              </div>
              {feedingType === "bottle" && (
                <div>
                  <Label>Amount (ml)</Label>
                  <Input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Optional"
                  />
                </div>
              )}
              <Button onClick={handleLogFeeding} className="w-full">
                Log Feeding
              </Button>
            </div>
          </Card>

          <div className="space-y-2">
            <h3 className="font-semibold">Recent Feedings</h3>
            {feedingData.slice(0, 5).map((feed) => (
              <Card key={feed.id} className="p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium capitalize">{feed.feeding_type.replace(/_/g, " ")}</p>
                    <p className="text-sm text-muted-foreground">
                      {format(new Date(feed.feeding_time), "MMM d, h:mm a")}
                    </p>
                  </div>
                  <div className="text-right text-sm">
                    {feed.duration_minutes && <p>{feed.duration_minutes} min</p>}
                    {feed.amount_ml && <p>{feed.amount_ml} ml</p>}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="sleep" className="space-y-4">
          <Card className="p-4">
            <h3 className="font-semibold mb-4">Log Sleep</h3>
            <div className="space-y-4">
              <div>
                <Label>Sleep Start</Label>
                <Input
                  type="datetime-local"
                  value={sleepStart}
                  onChange={(e) => setSleepStart(e.target.value)}
                />
              </div>
              <div>
                <Label>Sleep End</Label>
                <Input
                  type="datetime-local"
                  value={sleepEnd}
                  onChange={(e) => setSleepEnd(e.target.value)}
                />
              </div>
              <div>
                <Label>Quality</Label>
                <Select value={sleepQuality} onValueChange={setSleepQuality}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="peaceful">Peaceful</SelectItem>
                    <SelectItem value="restless">Restless</SelectItem>
                    <SelectItem value="fussy">Fussy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleLogSleep} className="w-full">
                Log Sleep
              </Button>
            </div>
          </Card>

          <div className="space-y-2">
            <h3 className="font-semibold">Recent Sleep Sessions</h3>
            {sleepData.slice(0, 5).map((sleep) => {
              const duration = sleep.sleep_end
                ? differenceInMinutes(new Date(sleep.sleep_end), new Date(sleep.sleep_start))
                : 0;
              return (
                <Card key={sleep.id} className="p-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{format(new Date(sleep.sleep_start), "MMM d, h:mm a")}</p>
                      <p className="text-sm text-muted-foreground capitalize">{sleep.sleep_quality}</p>
                    </div>
                    <p className="text-sm font-medium">
                      {Math.floor(duration / 60)}h {duration % 60}m
                    </p>
                  </div>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
